import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/ClubSocial.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const defaults = {
  club_webhook_enabled: false,
  club_webhook_url: '',
  club_webhook_secret: '',
  club_webhook_timeout_ms: 5000,
  club_webhook_max_attempts: 10,
  club_webhook_retry_minutes: '1,5,15,60,240',
  club_webhook_allow_private_http: true,
  club_snapshot_base_url: '',
  club_snapshot_secret: '',
  club_snapshot_url_ttl_minutes: 5,
  club_include_snapshot: true,
  club_inactive_member_expose_identity: true,
  club_event_retention_days: 90,
  club_failed_event_retention_days: 180
};
const toBoolean = value => value === true || value === 'true' || value === 1 || value === '1';

// Vocabulário da vertical, usado nos textos desta tela. A documentação em si
// mora em /integration-docs desde a Onda 64 — dentro da plataforma, atrás do
// mesmo login, com as duas verticais e download em PDF.
const DOCS = {
  clube_social: {
    titulo: 'Integração Clube Social',
    cadastro: 'a carga de associados que eles enviam',
    escopo: 'club_members:write',
  },
  condominio: {
    titulo: 'Integração Condomínio Residencial',
    cadastro: 'a carga de unidades e veículos que eles enviam',
    escopo: 'residents:write',
  },
};

const DOC_PAYLOAD = `{
  "event_id": "evt_f7720b33767042068e584f327a1c253a",
  "event_type": "club.plate.recognized.v1",
  "occurred_at": "2026-09-08T22:39:01.398Z",
  "recognition": { "plate": "BRQ0F75", "confidence": 1, "log_id": 1 },
  "camera": {
    "id": 3, "name": "CAM01-Entrada",
    "direction": "entrada", "ip_address": null
  },
  "vehicle": {
    "external_vehicle_id": null, "brand": null, "model": null
  },
  "person": {
    "classification": "visitor", "lookup_status": "not_found",
    "member_code": null, "name": null
  },
  "snapshot": null
}`;

function ClubSocial() {
  const { token, can, vertical } = useAuth();
  // Cada vertical aponta para a sua documentacao; o clube e o padrao.
  const doc = DOCS[vertical] || DOCS.clube_social;
  const [config, setConfig] = useState(defaults);
  const [metrics, setMetrics] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [configResult, metricsResult, outboxResult] = await Promise.all([
        axios.get(`${API_BASE}/api/club/config`, { headers }),
        axios.get(`${API_BASE}/api/club/metrics`, { headers }),
        axios.get(`${API_BASE}/api/club/outbox?status=failed&limit=20`, { headers })
      ]);
      const remote = configResult.data.data || {};
      setConfig({
        ...defaults, ...remote,
        club_webhook_enabled: toBoolean(remote.club_webhook_enabled),
        club_webhook_allow_private_http: toBoolean(remote.club_webhook_allow_private_http),
        club_include_snapshot: toBoolean(remote.club_include_snapshot),
        club_inactive_member_expose_identity: toBoolean(remote.club_inactive_member_expose_identity),
        club_webhook_secret: '', club_snapshot_secret: ''
      });
      setMetrics(metricsResult.data.data || null);
      setOutbox(outboxResult.data.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Não foi possível carregar a configuração da integração.' });
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  const update = (key, value) => setConfig(previous => ({ ...previous, [key]: value }));

  const save = async () => {
    try {
      setSaving(true); setMessage(null);
      const { data } = await axios.put(`${API_BASE}/api/club/config`, config, { headers });
      setMessage({ type: 'success', text: data.message || 'Configuração da integração salva com sucesso.' });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Não foi possível salvar a configuração.' });
    } finally { setSaving(false); }
  };

  const retry = async (id) => {
    try {
      await axios.post(`${API_BASE}/api/club/outbox/${id}/retry`, {}, { headers });
      setMessage({ type: 'success', text: 'Evento reenfileirado para nova tentativa.' });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Não foi possível reenviar o evento.' });
    }
  };

  const testWebhook = async () => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/club/webhook/test`, {}, { headers });
      setMessage({ type: 'success', text: `Webhook respondeu com HTTP ${data.data.http_status}.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Falha ao testar o webhook.' });
    }
  };

  if (!can('club_social')) return <div className="club-page"><h1>{doc.titulo}</h1><p>Acesso restrito a administradores.</p></div>;
  const metric = key => metrics?.[key] ?? '—';

  return (
    <div className="club-page">
      <div className="club-heading"><div><h1>{doc.titulo}</h1><p>Reconhecimento, cadastro externo e entrega resiliente de eventos. Tickets, cobrança e tolerância permanecem no sistema de gestão.</p></div><button className="club-secondary" onClick={load}>Atualizar dados</button></div>
      {message && <div className={`club-alert ${message.type}`}>{message.text}</div>}
      <section className="club-summary-grid">
        <article><span>Eventos pendentes</span><strong>{metric('pending')}</strong></article>
        <article><span>Em envio</span><strong>{metric('sending')}</strong></article>
        <article><span>Entregues</span><strong>{metric('delivered')}</strong></article>
        <article><span>Falhas exigindo ação</span><strong className={Number(metrics?.failed || 0) > 0 ? 'club-danger' : ''}>{metric('failed')}</strong></article>
      </section>
      {metrics?.last_sync && <div className="club-last-sync">Última sincronização externa: <strong>{metrics.last_sync.status}</strong> — iniciada em {new Date(metrics.last_sync.started_at).toLocaleString('pt-BR')}.</div>}

      <section className="club-card"><h2>Webhook de reconhecimento</h2><p>O evento assinado contém placa, classificação, marca/modelo, associado quando aplicável e câmera. Use HTTPS sempre que possível; HTTP é aceito apenas para IP privado ou localhost quando habilitado.</p>
        <div className="club-form-grid">
          <label className="club-toggle"><input type="checkbox" checked={config.club_webhook_enabled} onChange={e => update('club_webhook_enabled', e.target.checked)} /> Ativar envio de eventos</label>
          <label className="club-toggle"><input type="checkbox" checked={config.club_webhook_allow_private_http} onChange={e => update('club_webhook_allow_private_http', e.target.checked)} /> Permitir HTTP em rede privada</label>
          <label className="club-wide">URL do webhook<input value={config.club_webhook_url || ''} onChange={e => update('club_webhook_url', e.target.value)} placeholder="https://sistema-clube.local/api/fastalpr/eventos" /></label>
          <label>Segredo HMAC <small>{config.webhook_secret_configured ? 'Configurado; deixe em branco para preservar.' : 'Obrigatório para ativar o webhook.'}</small><input type="password" value={config.club_webhook_secret || ''} onChange={e => update('club_webhook_secret', e.target.value)} autoComplete="new-password" /></label>
          <label>Timeout (ms)<input type="number" min="500" max="30000" value={config.club_webhook_timeout_ms} onChange={e => update('club_webhook_timeout_ms', e.target.value)} /></label>
          <label>Tentativas máximas<input type="number" min="1" max="25" value={config.club_webhook_max_attempts} onChange={e => update('club_webhook_max_attempts', e.target.value)} /></label>
          <label>Intervalos de retentativa (minutos)<input value={config.club_webhook_retry_minutes || ''} onChange={e => update('club_webhook_retry_minutes', e.target.value)} /></label>
        </div>
      </section>
      <section className="club-card"><h2>Snapshots assinados e privacidade</h2><div className="club-form-grid">
        <label className="club-toggle"><input type="checkbox" checked={config.club_include_snapshot} onChange={e => update('club_include_snapshot', e.target.checked)} /> Incluir URL assinada de snapshot</label>
        <label className="club-toggle"><input type="checkbox" checked={config.club_inactive_member_expose_identity} onChange={e => update('club_inactive_member_expose_identity', e.target.checked)} /> Expor nome/código para associado inativo</label>
        <label className="club-wide">URL base interna do FastALPR<input value={config.club_snapshot_base_url || ''} onChange={e => update('club_snapshot_base_url', e.target.value)} placeholder="http://192.168.15.5" /></label>
        <label>Segredo de URL assinada <small>{config.snapshot_secret_configured ? 'Configurado; deixe em branco para preservar.' : 'Obrigatório para URLs assinadas.'}</small><input type="password" value={config.club_snapshot_secret || ''} onChange={e => update('club_snapshot_secret', e.target.value)} autoComplete="new-password" /></label>
        <label>Validade da URL (minutos)<input type="number" min="1" max="60" value={config.club_snapshot_url_ttl_minutes} onChange={e => update('club_snapshot_url_ttl_minutes', e.target.value)} /></label>
        <label>Retenção de entregas (dias)<input type="number" min="1" max="3650" value={config.club_event_retention_days} onChange={e => update('club_event_retention_days', e.target.value)} /></label>
        <label>Retenção de falhas (dias)<input type="number" min="1" max="3650" value={config.club_failed_event_retention_days} onChange={e => update('club_failed_event_retention_days', e.target.value)} /></label>
      </div></section>
      <div className="club-actions"><button className="club-secondary" onClick={testWebhook}>Testar webhook</button><button className="club-primary" disabled={saving || loading} onClick={save}>{saving ? 'Salvando...' : 'Salvar configuração'}</button></div>
      {/* A documentação mora aqui, na tela da própria integração: um link solto
          num e-mail se perde, e meses depois ninguém lembra onde estava o
          contrato que a equipe do sistema externo precisa seguir. */}
      <section className="club-card club-docs">
        <h2>Documentação da integração</h2>
        <p>O contrato que a equipe do sistema de gestão precisa seguir, nos três caminhos: {doc.cadastro}, o cadastro de visitantes com a autorização de cada visita, e o evento de placa que nós entregamos.</p>

        {/* Onda 64: a documentação passou a viver dentro da plataforma. Antes
            era um link para uma página publicada fora, que exigia autenticar num
            serviço de terceiros — barreira sem sentido para quem já tem login
            aqui. Agora é uma tela, com as duas verticais e download em PDF. */}
        <div className="club-doc-link">
          <div>
            <strong>Documentação completa, nas duas verticais</strong>
            <p>Dentro da plataforma, com download em PDF para enviar a quem for escrever o integrador.</p>
          </div>
          <div className="club-doc-url">
            <Link className="club-primary" to="/integration-docs">Abrir a documentação</Link>
          </div>
        </div>

        <details className="club-doc">
          <summary>Autenticação da API de carga</summary>
          <p>Chave gerada na tela <em>API de Integração</em>, enviada em <code>X-API-Key</code> ou <code>Authorization: Bearer</code>. Escopo necessário para a carga: <code>{doc.escopo}</code>. As rotas de escrita exigem o cabeçalho <code>Idempotency-Key</code> (até 128 caracteres, resposta guardada por 24 h).</p>
        </details>

        <details className="club-doc">
          <summary>Rotas da carga de associados</summary>
          <table className="club-doc-table"><tbody>
            <tr><td><code>PUT</code></td><td><code>/api/integration/club/members/:member_code</code></td><td>um associado e seus veículos</td></tr>
            <tr><td><code>POST</code></td><td><code>/api/integration/club/members/batch</code></td><td>de 1 a 500 por chamada</td></tr>
            <tr><td><code>POST</code></td><td><code>/api/integration/club/sync-runs</code></td><td>abre uma carga completa</td></tr>
            <tr><td><code>POST</code></td><td><code>/api/integration/club/sync-runs/:id/complete</code></td><td>encerra e inativa os ausentes</td></tr>
            <tr><td><code>GET</code></td><td><code>/api/integration/club/members</code></td><td>confere o cadastro carregado</td></tr>
            <tr><td><code>GET</code></td><td><code>/api/integration/club/events</code></td><td>histórico de reconhecimentos</td></tr>
          </tbody></table>
          <p className="club-doc-note">Obrigatórios: <code>member_code</code> (≤ 64), <code>name</code> (≤ 180) e, por veículo, <code>external_vehicle_id</code> (≤ 100) e <code>plate</code>. Uma placa ativa pertence a um único veículo — repetir dá <code>409</code>. Sincronização completa nunca apaga: o ausente vira inativo.</p>
        </details>

        <details className="club-doc">
          <summary>Evento entregue pelo webhook</summary>
          <p><code>POST</code> com <code>X-FastALPR-Event-Id</code>, <code>X-FastALPR-Timestamp</code> e <code>X-FastALPR-Signature: sha256=…</code>. A assinatura é HMAC-SHA256 do segredo sobre <code>{'{timestamp}.{corpo bruto}'}</code>.</p>
          <pre className="club-doc-pre">{DOC_PAYLOAD}</pre>
          <p className="club-doc-note">Classificação: <code>member</code>, <code>inactive_member</code> ou <code>visitor</code>. Sem cadastro carregado, toda placa sai como <code>visitor</code>.</p>
        </details>

        <details className="club-doc">
          <summary>Resposta esperada e retentativas</summary>
          <p>Qualquer <strong>2xx</strong> confirma o recebimento. O evento é gravado antes da entrega, então nada se perde se o destino estiver fora do ar. As retentativas seguem os intervalos configurados acima — hoje {config.club_webhook_retry_minutes || '1,5,15,60,240'} minutos, até {config.club_webhook_max_attempts || 10} tentativas.</p>
          <p className="club-doc-note">Uma retentativa reenvia o <strong>mesmo</strong> <code>event_id</code>. O receptor deve tratá-lo como idempotente e responder 2xx sem reprocessar.</p>
        </details>
      </section>

      <section className="club-card"><h2>Falhas da outbox</h2><p>Eventos não entregues permanecem auditáveis e podem ser reenviados manualmente após a correção do sistema externo.</p>
        {loading ? <p>Carregando...</p> : outbox.length === 0 ? <p className="club-empty">Nenhuma falha pendente.</p> : <div className="club-table-wrap"><table><thead><tr><th>Evento</th><th>Classificação</th><th>Tentativas</th><th>Erro</th><th>Ação</th></tr></thead><tbody>{outbox.map(event => <tr key={event.id}><td><code>{event.event_id.slice(0, 16)}…</code></td><td>{event.classification}</td><td>{event.attempt_count}</td><td>{event.last_error || '—'}</td><td><button className="club-secondary" onClick={() => retry(event.id)}>Reenviar</button></td></tr>)}</tbody></table></div>}
      </section>
    </div>
  );
}
export default ClubSocial;
